// ============================================================================
// INVENTORY SETTINGS (ADR-0058 slice 1) — per-entity settings page.
//   a) Inventory policy: accounting mode (changes only from a financial-year
//      start), negative-stock policy, costing method; plus its history.
//   b) Default ledgers stock postings use, per purpose.
//   c) Ledger overrides by category / item.
//   d) The auto-provisioned transit location.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, type ActionButton } from 'components/modals';
import {
  SyutGrid, type SyutGridColumn, type ActionMenuItem, SyutSelect, type SelectOption,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useInventorySettings, useLedgerOverrides } from 'hooks/api';
import {
  inventorySettingsService, coaLedgerService, itemCategoryService, itemService,
} from 'api/services';
import {
  INVENTORY_ACCOUNTING_MODES,
  type InventoryAccountingMode, type NegativeStockPolicy, type InventorySettingDto,
  type InventorySettingsOverviewDto, type InventoryLedgerDefaultDto, type InventoryFinancialYearDto,
  type LedgerOverrideDto, type LedgerOverrideQueryParams, type SaveInventorySettingRequest,
  type SaveLedgerOverrideRequest,
} from 'api/types/inventory.types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type ApiError = { response?: { status?: number; data?: { message?: string } } };
const errorMessage = (err: unknown, fallback: string) => (err as ApiError)?.response?.data?.message || fallback;

type IconComponent = React.ComponentType<{ size?: number }>;
const ICONS = IconSystem as unknown as Record<string, IconComponent | undefined>;

/** dd MMM yyyy for a date-only ISO string (no shared formatter exists in src/utils). */
const fmtDate = (s?: string | null) =>
  (s ? new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const fmtWhen = (s?: string | null) =>
  (s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const dateKey = (s?: string | null) => (s || '').slice(0, 10);

const modeMeta = (m?: InventoryAccountingMode | null) => INVENTORY_ACCOUNTING_MODES.find(x => x.value === m);
const modeLabel = (m?: InventoryAccountingMode | null) => modeMeta(m)?.label ?? (m || '');

const NEGATIVE_STOCK_OPTIONS: { value: NegativeStockPolicy; label: string }[] = [
  { value: 'BLOCK', label: 'Block issues that would make stock negative' },
  { value: 'WARN', label: 'Allow with a warning' },
];
const negativeLabel = (p?: NegativeStockPolicy | null) => NEGATIVE_STOCK_OPTIONS.find(o => o.value === p)?.label ?? (p || '');

const COSTING_LABEL = 'Weighted average (moving, per item per entity)';

/** GL ledger picker — same loader as EntityAccountingConfigScreen. */
const loadLedgerOptions = async (input: string): Promise<SelectOption[]> => {
  try {
    const res = await coaLedgerService.getAll({
      ledgerKind: 'GL', pageSize: 50, searchTerm: input || undefined,
    });
    return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
  } catch {
    return [];
  }
};

const loadCategoryOptions = async (input: string): Promise<SelectOption[]> => {
  try {
    const res = await itemCategoryService.getAll({ searchTerm: input || undefined, pageSize: 50 });
    return res.data.map(c => ({ value: c.id, label: c.path ?? c.categoryName }));
  } catch {
    return [];
  }
};

const loadItemOptions = async (input: string): Promise<SelectOption[]> => {
  try {
    const res = await itemService.getAll({ searchTerm: input || undefined, pageSize: 50 });
    return res.data.map(i => ({ value: i.id, label: `${i.itemCode} - ${i.itemName}` }));
  } catch {
    return [];
  }
};

// ---------------------------------------------------------------------------
// Layout pieces
// ---------------------------------------------------------------------------

const Card: React.FC<{ title: string; description?: string; actions?: React.ReactNode; children: React.ReactNode }> = ({
  title, description, actions, children,
}) => (
  <div style={{
    border: '1px solid var(--border-color)', borderRadius: '0.75rem', background: 'var(--bg-primary)',
    padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem',
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h2>
        {description && (
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{description}</p>
        )}
      </div>
      {actions && <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>{actions}</div>}
    </div>
    {children}
  </div>
);

const InfoRow: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div style={{
    display: 'flex', gap: '1.5rem', padding: '0.6rem 0', borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap',
  }}>
    <div style={{ width: 200, flexShrink: 0, fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</div>
    <div style={{ flex: 1, minWidth: 200 }}>
      <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{children}</div>
      {hint && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>{hint}</div>}
    </div>
  </div>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    display: 'flex', gap: '0.5rem', alignItems: 'flex-start', fontSize: '0.8rem', color: 'var(--text-secondary)',
    background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem',
    padding: '0.5rem 0.75rem',
  }}>
    <span style={{ flexShrink: 0, marginTop: '0.1rem' }}><IconSystem.info size={14} /></span>
    <span>{children}</span>
  </div>
);

const Badge: React.FC<{ children: React.ReactNode; tone?: 'danger' | 'info' | 'neutral' }> = ({ children, tone = 'neutral' }) => {
  const palette = {
    danger: { background: 'var(--danger-bg, #fee2e2)', color: 'var(--danger, #b91c1c)' },
    info: { background: 'var(--info-bg, #dbeafe)', color: 'var(--info, #1d4ed8)' },
    neutral: { background: 'var(--bg-secondary)', color: 'var(--text-secondary)' },
  }[tone];
  return (
    <span className="badge" style={{ ...palette, fontSize: '0.7rem', fontWeight: 600 }}>{children}</span>
  );
};

const LoadingOverlay: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
  }}>
    <div style={{
      width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
      borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
    <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
  </div>
);

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '0.45rem 0.6rem', fontSize: '0.75rem', fontWeight: 600,
  color: 'var(--text-secondary)', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)',
  whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
  padding: '0.45rem 0.6rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.83rem',
  color: 'var(--text-primary)', verticalAlign: 'middle',
};

const radioCardStyle = (active: boolean, disabled = false): React.CSSProperties => ({
  display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.65rem 0.8rem',
  border: `1px solid ${active ? 'var(--primary, #2563eb)' : 'var(--form-border-color)'}`,
  borderRadius: '0.5rem', cursor: disabled ? 'default' : 'pointer',
  background: active ? 'var(--bg-secondary)' : 'var(--bg-primary)',
});

// ---------------------------------------------------------------------------
// POLICY MODAL
// ---------------------------------------------------------------------------

interface PolicyForm {
  effectiveFrom: string;
  accountingMode: InventoryAccountingMode;
  negativeStockPolicy: NegativeStockPolicy;
}

interface PolicyModalProps {
  isOpen: boolean;
  overview: InventorySettingsOverviewDto;
  onSaved: (result: InventorySettingsOverviewDto) => void;
  onClose: () => void;
}

const fyLabel = (fy: InventoryFinancialYearDto) => `${fy.fyName} (from ${fmtDate(fy.startDate)})`;

const PolicyModal: React.FC<PolicyModalProps> = ({ isOpen, overview, onSaved, onClose }) => {
  const [form, setForm] = useState<PolicyForm>({ effectiveFrom: '', accountingMode: 'PERPETUAL', negativeStockPolicy: 'BLOCK' });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const snapshotRef = useRef('');
  const overviewRef = useRef(overview); overviewRef.current = overview;

  const isFirst = !overview.current;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const ov = overviewRef.current;
    const cur = ov.current;
    const years = [...ov.financialYears].sort((a, b) => dateKey(a.startDate).localeCompare(dateKey(b.startDate)));
    const today = new Date().toISOString().slice(0, 10);
    let effectiveFrom = '';
    if (cur) {
      // Changing the policy: suggest the first year that starts after the one in force.
      const next = years.find(y => dateKey(y.startDate) > dateKey(cur.effectiveFrom));
      effectiveFrom = dateKey(next?.startDate ?? cur.effectiveFrom);
    } else {
      const containing = years.find(y => dateKey(y.startDate) <= today && dateKey(y.endDate) >= today);
      effectiveFrom = dateKey(containing?.startDate ?? years[0]?.startDate ?? '');
    }
    const data: PolicyForm = {
      effectiveFrom,
      accountingMode: cur?.accountingMode ?? ov.suggestedAccountingMode,
      negativeStockPolicy: cur?.negativeStockPolicy ?? 'BLOCK',
    };
    setForm(data);
    snapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => (snapshotRef.current ? JSON.stringify(form) !== snapshotRef.current : false), [form]);

  const fyOptions: SelectOption[] = useMemo(
    () => [...overview.financialYears]
      .sort((a, b) => dateKey(a.startDate).localeCompare(dateKey(b.startDate)))
      .map(fy => ({ value: dateKey(fy.startDate), label: fyLabel(fy) })),
    [overview.financialYears],
  );

  const replacing: InventorySettingDto | undefined = useMemo(
    () => overview.history.find(h => dateKey(h.effectiveFrom) === form.effectiveFrom),
    [overview.history, form.effectiveFrom],
  );

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!form.effectiveFrom) errors.effectiveFrom = 'Pick the financial year the policy starts from';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const body: SaveInventorySettingRequest = {
        effectiveFrom: form.effectiveFrom,
        accountingMode: form.accountingMode,
        costingMethod: 'WEIGHTED_AVERAGE',
        negativeStockPolicy: form.negativeStockPolicy,
        closingStockFrequency: 'MONTHLY',
        ...(replacing ? { rowVersion: replacing.rowVersion } : {}),
      };
      const result = await inventorySettingsService.savePolicy(body);
      setSaving(false);
      notificationService.toast.success('Inventory policy saved.');
      onSaved(result);
      onClose();
    } catch (err) {
      setSaving(false);
      notificationService.toast.error(errorMessage(err, 'Could not save the inventory policy.'));
    }
  }, [form, replacing, onSaved, onClose]);

  if (!isOpen) return null;

  const showSuggestion = isFirst && !!overview.countryCode;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {saving && <LoadingOverlay text="Saving..." />}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{isFirst ? 'Set up inventory policy' : 'Change inventory policy'}</h2>
          <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div className="form-group" data-field="effectiveFrom">
            <SyutSelect
              label={<>Effective from <span className="text-danger">*</span></>}
              options={fyOptions}
              value={form.effectiveFrom}
              onChange={v => { setFieldErrors({}); setForm(p => ({ ...p, effectiveFrom: (v as string) || '' })); }}
              placeholder={fyOptions.length ? 'Pick a financial year' : 'No financial years set up'}
              isClearable={false}
              menuPortalTarget={document.body}
              error={fieldErrors.effectiveFrom}
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
              The accounting mode can change only from the start of a financial year.
            </div>
            {replacing && (
              <div style={{ marginTop: '0.5rem' }}>
                <Note>Replaces the policy that starts on this date ({modeLabel(replacing.accountingMode)}).</Note>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Accounting mode <span className="text-danger">*</span></label>
            {showSuggestion && (
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                Suggested for {overview.countryCode}: <strong>{modeLabel(overview.suggestedAccountingMode)}</strong>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }} role="radiogroup">
              {INVENTORY_ACCOUNTING_MODES.map(m => {
                const active = form.accountingMode === m.value;
                return (
                  <label key={m.value} style={radioCardStyle(active)}>
                    <input type="radio" name="inv-accounting-mode" checked={active} style={{ marginTop: '0.2rem' }}
                      onChange={() => setForm(p => ({ ...p, accountingMode: m.value }))} />
                    <span>
                      <span style={{ display: 'block', fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {m.label}
                        {showSuggestion && m.value === overview.suggestedAccountingMode && (
                          <span style={{ marginLeft: '0.5rem' }}><Badge tone="info">Suggested</Badge></span>
                        )}
                      </span>
                      <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '0.1rem' }}>
                        {m.hint}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Negative stock</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }} role="radiogroup">
              {NEGATIVE_STOCK_OPTIONS.map(o => (
                <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                  <input type="radio" name="inv-negative-stock" checked={form.negativeStockPolicy === o.value}
                    onChange={() => setForm(p => ({ ...p, negativeStockPolicy: o.value }))} />
                  {o.label}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Costing method</label>
            <input type="text" className="form-input" value="Weighted average" readOnly
              style={{ background: 'var(--bg-secondary)' }} />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !fyOptions.length}>
            <IconSystem.check size={16} /> Save
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save inventory policy?"
        message={`${modeLabel(form.accountingMode)} accounting from ${fmtDate(form.effectiveFrom)}${replacing ? ', replacing the policy that starts on this date' : ''}. Save?`}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void handleConfirmSave(); }}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// OVERRIDE MODAL
// ---------------------------------------------------------------------------

type TargetType = 'CATEGORY' | 'ITEM';

interface OverrideForm {
  targetType: TargetType;
  categoryId: string;
  itemId: string;
  purposeCode: string;
  ledgerId: string;
}

const EMPTY_OVERRIDE: OverrideForm = { targetType: 'CATEGORY', categoryId: '', itemId: '', purposeCode: '', ledgerId: '' };
/** Overridable besides the INV_* purposes: the expense ledger a non-stock item's bill line debits. */
const PURCHASE_EXPENSE_PURPOSE = 'PURCHASE_EXPENSE';
/** ... and the income ledger an item's sales invoice / credit note line credits. */
const SALES_INCOME_PURPOSE = 'SALES_INCOME';

interface OverrideModalProps {
  isOpen: boolean;
  purposes: InventoryLedgerDefaultDto[];
  onSaved: () => void;
  onClose: () => void;
}

const OverrideModal: React.FC<OverrideModalProps> = ({ isOpen, purposes, onSaved, onClose }) => {
  const [form, setForm] = useState<OverrideForm>(EMPTY_OVERRIDE);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const snapshotRef = useRef('');

  useEffect(() => {
    if (!isOpen) return;
    const data = { ...EMPTY_OVERRIDE };
    setForm(data);
    snapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => (snapshotRef.current ? JSON.stringify(form) !== snapshotRef.current : false), [form]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(prev => {
      if (!prev[f]) return prev;
      const next = { ...prev }; delete next[f]; return next;
    });
  }, []);

  // The INV_* purposes come from the overview; PURCHASE_EXPENSE (non-stock items' bill lines) and
  // SALES_INCOME (item sales lines) can be overridden per category / item too, but their defaults
  // live under Entity Accounting Config, so the overview does not list them — they are added here.
  const purposeOptions: SelectOption[] = useMemo(
    () => [
      ...purposes.map(p => ({ value: p.purposeCode, label: p.purposeName })),
      ...(purposes.some(p => p.purposeCode === PURCHASE_EXPENSE_PURPOSE) ? [] : [{ value: PURCHASE_EXPENSE_PURPOSE, label: 'Purchase expense (non-stock items)' }]),
      ...(purposes.some(p => p.purposeCode === SALES_INCOME_PURPOSE) ? [] : [{ value: SALES_INCOME_PURPOSE, label: 'Sales income (item sales)' }]),
    ],
    [purposes],
  );

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (form.targetType === 'CATEGORY' && !form.categoryId) errors.categoryId = 'Pick a category';
    if (form.targetType === 'ITEM' && !form.itemId) errors.itemId = 'Pick an item';
    if (!form.purposeCode) errors.purposeCode = 'Pick a purpose';
    if (!form.ledgerId) errors.ledgerId = 'Pick a ledger';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const body: SaveLedgerOverrideRequest = {
        categoryId: form.targetType === 'CATEGORY' ? form.categoryId : null,
        itemId: form.targetType === 'ITEM' ? form.itemId : null,
        purposeCode: form.purposeCode,
        ledgerId: form.ledgerId,
      };
      await inventorySettingsService.saveOverride(body);
      setSaving(false);
      notificationService.toast.success('Ledger override saved.');
      onSaved();
      onClose();
    } catch (err) {
      setSaving(false);
      notificationService.toast.error(errorMessage(err, 'Could not save the ledger override.'));
    }
  }, [form, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {saving && <LoadingOverlay text="Saving..." />}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Add ledger override</h2>
          <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div className="form-group">
            <label className="form-label">For</label>
            <div style={{ display: 'flex', gap: '1.25rem' }} role="radiogroup">
              {(['CATEGORY', 'ITEM'] as TargetType[]).map(t => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  <input type="radio" name="override-target" checked={form.targetType === t}
                    onChange={() => {
                      clearFieldError('categoryId'); clearFieldError('itemId');
                      setForm(p => ({ ...p, targetType: t, categoryId: '', itemId: '' }));
                    }} />
                  {t === 'CATEGORY' ? 'Category' : 'Item'}
                </label>
              ))}
            </div>
          </div>

          {form.targetType === 'CATEGORY' ? (
            <div className="form-group" data-field="categoryId">
              <SyutSelect key="target-category"
                label={<>Category <span className="text-danger">*</span></>}
                isAsync loadOptions={loadCategoryOptions} defaultOptions cacheOptions={false}
                menuPortalTarget={document.body}
                value={form.categoryId}
                onChange={v => { clearFieldError('categoryId'); setForm(p => ({ ...p, categoryId: (v as string) || '' })); }}
                placeholder="Search categories…" isClearable
                error={fieldErrors.categoryId}
                helperText="Applies to its sub-categories too." />
            </div>
          ) : (
            <div className="form-group" data-field="itemId">
              <SyutSelect key="target-item"
                label={<>Item <span className="text-danger">*</span></>}
                isAsync loadOptions={loadItemOptions} defaultOptions cacheOptions={false}
                menuPortalTarget={document.body}
                value={form.itemId}
                onChange={v => { clearFieldError('itemId'); setForm(p => ({ ...p, itemId: (v as string) || '' })); }}
                placeholder="Search items…" isClearable
                error={fieldErrors.itemId} />
            </div>
          )}

          <div className="form-group" data-field="purposeCode">
            <SyutSelect
              label={<>Purpose <span className="text-danger">*</span></>}
              options={purposeOptions}
              value={form.purposeCode}
              onChange={v => { clearFieldError('purposeCode'); setForm(p => ({ ...p, purposeCode: (v as string) || '' })); }}
              placeholder="Pick a purpose" isClearable={false}
              menuPortalTarget={document.body}
              error={fieldErrors.purposeCode} />
          </div>

          <div className="form-group" data-field="ledgerId">
            <SyutSelect
              label={<>Ledger <span className="text-danger">*</span></>}
              isAsync loadOptions={loadLedgerOptions} defaultOptions cacheOptions={false}
              menuPortalTarget={document.body}
              value={form.ledgerId}
              onChange={v => { clearFieldError('ledgerId'); setForm(p => ({ ...p, ledgerId: (v as string) || '' })); }}
              placeholder="Pick a ledger" isClearable
              error={fieldErrors.ledgerId} />
          </div>

          <Note>An existing override for the same {form.targetType === 'CATEGORY' ? 'category' : 'item'} and purpose is replaced.</Note>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <IconSystem.check size={16} /> Save
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save ledger override?"
        message="Postings for this purpose will use the chosen ledger instead of the default. Save?"
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={() => { void handleConfirmSave(); }}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// SCREEN
// ---------------------------------------------------------------------------

interface InventorySettingsScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const TARGET_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All' },
  { value: 'CATEGORY', label: 'Category' },
  { value: 'ITEM', label: 'Item' },
];

const InventorySettingsScreen: React.FC<InventorySettingsScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('inventory-settings');
  const canEdit = can('UPDATE');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const { data: overview, setData, loading, error, refetch } = useInventorySettings();
  const [policyOpen, setPolicyOpen] = useState(false);
  const [busyPurpose, setBusyPurpose] = useState<string | null>(null);

  // --- Overrides grid (server-paged) ---
  const [overrideParams, setOverrideParams] = useState<LedgerOverrideQueryParams>({ page: 1, pageSize: 10 });
  const {
    data: overrides, loading: overridesLoading, pagination: overridePagination, refetch: refetchOverrides,
  } = useLedgerOverrides(overrideParams);
  const [overrideSearch, setOverrideSearch] = useState('');
  const [overrideOpen, setOverrideOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(
      () => setOverrideParams(p => ({ ...p, searchTerm: overrideSearch || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [overrideSearch]);

  const current = overview?.current ?? null;
  const currentMode = current?.accountingMode ?? null;

  // --- Default ledgers: set / clear immediately ---
  const patchDefault = useCallback((purposeCode: string, next: Partial<InventoryLedgerDefaultDto>) => {
    setData(prev => (prev
      ? { ...prev, ledgerDefaults: prev.ledgerDefaults.map(d => (d.purposeCode === purposeCode ? { ...d, ...next } : d)) }
      : prev));
  }, [setData]);

  const handleSetDefault = useCallback(async (row: InventoryLedgerDefaultDto, ledgerId: string) => {
    if (!ledgerId || ledgerId === row.ledgerId) return;
    setBusyPurpose(row.purposeCode);
    try {
      const saved = await inventorySettingsService.setLedgerDefault({ purposeCode: row.purposeCode, ledgerId });
      patchDefault(row.purposeCode, saved);
      notificationService.toast.success(`Default ledger for "${row.purposeName}" updated.`);
    } catch (err) {
      notificationService.toast.error(errorMessage(err, 'Could not update the default ledger.'));
    } finally {
      setBusyPurpose(null);
    }
  }, [patchDefault]);

  const handleClearDefault = useCallback(async (row: InventoryLedgerDefaultDto) => {
    setBusyPurpose(row.purposeCode);
    try {
      await inventorySettingsService.clearLedgerDefault(row.purposeCode);
      patchDefault(row.purposeCode, { ledgerId: null, ledgerCode: null, ledgerName: null });
      notificationService.toast.success(`Default ledger for "${row.purposeName}" cleared.`);
    } catch (err) {
      notificationService.toast.error(errorMessage(err, 'Could not clear the default ledger.'));
    } finally {
      setBusyPurpose(null);
    }
  }, [patchDefault]);

  // --- Overrides ---
  const handleRemoveOverride = useCallback(async (o: LedgerOverrideDto) => {
    const target = o.itemId ? `${o.itemCode ?? ''} - ${o.itemName ?? ''}` : (o.categoryName ?? '');
    const ok = await confirmDelete({
      title: 'Remove override?',
      message: `Remove the "${o.purposeName ?? o.purposeCode}" override for ${target}? Postings will fall back to the ${o.itemId ? 'category or default' : 'default'} ledger.`,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      await inventorySettingsService.deleteOverride(o.id);
      notificationService.toast.success('Ledger override removed.');
      refetchOverrides();
    } catch (err) {
      notificationService.toast.error(errorMessage(err, 'Could not remove the override.'));
    }
  }, [confirmDelete, refetchOverrides]);

  const overrideColumns: SyutGridColumn<LedgerOverrideDto>[] = useMemo(() => [
    {
      id: 'target', header: 'For', size: 300,
      cell: ({ row }) => {
        const o = row.original;
        return o.itemId ? (
          <span style={{ display: 'inline-flex', gap: '0.4rem', alignItems: 'center' }}>
            <Badge tone="info">Item</Badge>{`${o.itemCode ?? ''} - ${o.itemName ?? ''}`}
          </span>
        ) : (
          <span style={{ display: 'inline-flex', gap: '0.4rem', alignItems: 'center' }}>
            <Badge>Category</Badge>{o.categoryName ?? o.categoryCode ?? ''}
          </span>
        );
      },
    },
    { id: 'purpose', header: 'Purpose', size: 200, cell: ({ row }) => row.original.purposeName ?? row.original.purposeCode },
    {
      id: 'ledger', header: 'Ledger', size: 260,
      cell: ({ row }) => `${row.original.ledgerCode ?? ''} - ${row.original.ledgerName ?? ''}`,
    },
    {
      id: 'updatedBy', header: 'Updated by', size: 200,
      cell: ({ row }) => {
        const o = row.original;
        if (!o.updatedByName && !o.updatedAt) return '—';
        return [o.updatedByName, fmtWhen(o.updatedAt)].filter(Boolean).join(' · ');
      },
    },
  ], []);

  const overrideRowActions = useCallback((o: LedgerOverrideDto): ActionMenuItem[] => (canEdit
    ? [{ id: 'remove', label: 'Remove', icon: <IconSystem.trash size={18} />, onClick: () => { void handleRemoveOverride(o); }, variant: 'danger' }]
    : []), [canEdit, handleRemoveOverride]);

  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(() => { void refetch(); refetchOverrides(); }),
  ];

  const HeaderIcon: IconComponent = (moduleIcon && ICONS[moduleIcon]) || IconSystem.settings;

  const history = useMemo(
    () => [...(overview?.history || [])].sort((a, b) => dateKey(b.effectiveFrom).localeCompare(dateKey(a.effectiveFrom))),
    [overview?.history],
  );

  // ---- Render helpers ----

  const renderPolicy = (ov: InventorySettingsOverviewDto) => (
    <Card
      title="Inventory policy"
      description="How stock is valued and whether it posts to the books."
      actions={canEdit && current ? (
        <button className="btn btn-secondary" onClick={() => setPolicyOpen(true)}
          style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
          <IconSystem.edit size={14} /> Change from a year…
        </button>
      ) : undefined}
    >
      {!current ? (
        <div style={{ textAlign: 'center', padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' }}>
          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>No inventory policy yet</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', maxWidth: 460 }}>
            Choose how stock is accounted for before recording stock movements.
          </div>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => setPolicyOpen(true)}>
              <IconSystem.add size={16} /> Set up inventory policy
            </button>
          )}
        </div>
      ) : (
        <div>
          <InfoRow label="Accounting mode" hint={modeMeta(current.accountingMode)?.hint}>
            <strong>{modeLabel(current.accountingMode)}</strong>
          </InfoRow>
          <InfoRow label="Since">
            {fmtDate(current.effectiveFrom)}
            {current.financialYearName ? ` (${current.financialYearName})` : ''}
          </InfoRow>
          <InfoRow label="Costing method">{COSTING_LABEL}</InfoRow>
          <InfoRow label="Negative stock">{negativeLabel(current.negativeStockPolicy)}</InfoRow>
          {current.accountingMode === 'PERIODIC' && (
            <InfoRow label="Closing stock">Monthly</InfoRow>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>History</div>
          <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.5rem', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>From</th>
                  <th style={thStyle}>Year</th>
                  <th style={thStyle}>Mode</th>
                  <th style={thStyle}>Negative stock</th>
                  <th style={thStyle}>Updated by / at</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td style={tdStyle}>
                      {fmtDate(h.effectiveFrom)}
                      {current && h.id === current.id && <span style={{ marginLeft: '0.4rem' }}><Badge tone="info">In force</Badge></span>}
                    </td>
                    <td style={tdStyle}>{h.financialYearName || '—'}</td>
                    <td style={tdStyle}>{modeLabel(h.accountingMode)}</td>
                    <td style={tdStyle}>{h.negativeStockPolicy === 'BLOCK' ? 'Block' : 'Warn'}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>
                      {[h.updatedByName, fmtWhen(h.updatedAt)].filter(Boolean).join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {ov.financialYears.length === 0 && (
        <Note>No financial years are set up for this entity yet — add one before setting the policy.</Note>
      )}
    </Card>
  );

  const renderDefaults = (ov: InventorySettingsOverviewDto) => (
    <Card title="Default ledgers" description="The ledgers stock postings use for each purpose, unless a category or item overrides them.">
      {currentMode === 'NONE' && <Note>Quantity only: no ledgers are needed.</Note>}
      {ov.ledgerDefaults.length === 0 && (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No posting purposes configured.</div>
      )}
      <div>
        {ov.ledgerDefaults.map(d => {
          const unused = !!currentMode && !d.usedInModes.includes(currentMode);
          const usedIn = d.usedInModes.map(m => modeLabel(m)).join(', ');
          const seed = d.ledgerId
            ? [{ value: d.ledgerId, label: `${d.ledgerCode ?? ''} - ${d.ledgerName ?? ''}` }]
            : undefined;
          return (
            <div key={d.purposeCode} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem',
              padding: '0.75rem 0', borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap',
              opacity: unused ? 0.6 : 1,
            }}>
              <div style={{ minWidth: 220, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {d.purposeName}
                  {d.required && <Badge tone="danger">Required</Badge>}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                  Used in: {usedIn || '—'}
                  {unused && currentMode && <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>Not used under {modeLabel(currentMode)}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 300, maxWidth: 440, width: 420 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SyutSelect key={`${d.purposeCode}-${d.ledgerId ?? 'none'}`}
                    isAsync loadOptions={loadLedgerOptions} defaultOptions cacheOptions={false}
                    seedOptions={seed} menuPortalTarget={document.body}
                    value={d.ledgerId ?? ''}
                    onChange={v => { if (v) void handleSetDefault(d, v as string); }}
                    placeholder="Not set — pick a ledger" isClearable={false} size="sm"
                    isDisabled={busyPurpose === d.purposeCode || !canEdit} />
                </div>
                {d.ledgerId && canEdit && (
                  <button className="btn btn-secondary" disabled={busyPurpose === d.purposeCode}
                    onClick={() => { void handleClearDefault(d); }}
                    style={{ fontSize: '0.78rem', padding: '0.3rem 0.6rem' }}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );

  const renderOverrides = () => (
    <Card
      title="Ledger overrides"
      description="A category or an item can post to its own ledgers instead of the defaults. An item's override wins over its category's; a category's applies to its sub-categories."
      actions={canEdit ? (
        <button className="btn btn-primary" onClick={() => setOverrideOpen(true)}
          style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
          <IconSystem.add size={14} /> Add override
        </button>
      ) : undefined}
    >
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ width: 180 }}>
          <SyutSelect label="Type" options={TARGET_FILTER_OPTIONS} value={overrideParams.targetType ?? ''}
            onChange={v => setOverrideParams(p => ({ ...p, targetType: ((v as string) || undefined) as LedgerOverrideQueryParams['targetType'], page: 1 }))}
            isClearable={false} size="sm" menuPortalTarget={document.body} />
        </div>
        <div style={{ flex: 1, minWidth: 220, maxWidth: 360 }}>
          <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Search</label>
          <input type="text" className="form-input" value={overrideSearch}
            onChange={e => setOverrideSearch(e.target.value)} placeholder="Category, item, purpose or ledger…"
            style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
        </div>
      </div>
      <div style={{ height: 420, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <SyutGrid data={overrides} columns={overrideColumns}
          enablePagination manualPagination totalCount={overridePagination.totalItems}
          currentPage={(overrideParams.page ?? 1) - 1} pageSize={overrideParams.pageSize ?? 10}
          onPageChange={(i) => setOverrideParams(p => ({ ...p, page: i + 1 }))}
          onPageSizeChange={(s) => setOverrideParams(p => ({ ...p, pageSize: s, page: 1 }))}
          rowActions={canEdit ? overrideRowActions : undefined}
          emptyMessage={overridesLoading ? 'Loading...' : 'No overrides — every posting uses the default ledgers.'} />
      </div>
    </Card>
  );

  const renderTransit = (ov: InventorySettingsOverviewDto) => (
    <Card title="Transit location">
      <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)' }}>
        {ov.transitLocation
          ? `Stock in transit is held in ${ov.transitLocation.locationName} (${ov.transitLocation.locationCode})`
          : 'Created automatically with the first stock location.'}
      </div>
    </Card>
  );

  return (
    <EnterpriseScreenShell
      title="Inventory Settings"
      description="How stock is accounted for, and the ledgers stock postings use"
      icon={<HeaderIcon size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      ConfirmDialogPortal={ConfirmDialogPortal}
    >
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: '1rem', paddingBottom: '1rem' }}>
        {loading && !overview && (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</div>
        )}
        {!loading && error && !overview && (
          <Card title="Could not load inventory settings">
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {errorMessage(error, error.message || 'The settings could not be loaded.')}
            </div>
            <div>
              <button className="btn btn-primary" onClick={() => { void refetch(); }}>
                <IconSystem.refresh size={14} /> Retry
              </button>
            </div>
          </Card>
        )}
        {overview && (
          <>
            {renderPolicy(overview)}
            {renderDefaults(overview)}
            {renderOverrides()}
            {renderTransit(overview)}
          </>
        )}
      </div>

      {overview && (
        <PolicyModal isOpen={policyOpen} overview={overview}
          onSaved={result => setData(result)}
          onClose={() => setPolicyOpen(false)} />
      )}
      <OverrideModal isOpen={overrideOpen} purposes={overview?.ledgerDefaults ?? []}
        onSaved={refetchOverrides}
        onClose={() => setOverrideOpen(false)} />
    </EnterpriseScreenShell>
  );
};

export default InventorySettingsScreen;
